/**
 * SubscriptionCostsSection Component
 *
 * - Distribution paid by Investor: always visible, own row
 * - Sales Fee paid by Investor: always visible, stacked below Distribution
 * - On Issue date / On Maturity date: only shown when Sales Fee = Yes,
 *   rendered inside a KanbanContainer frame (like Basket Assets), both mandatory
 */
import React, { useCallback } from 'react';
import { Row, Col, FormGroup, FormLabel } from 'react-bootstrap';
import StyledFormText, { KanbanContainer, StyledCardHeader, StyledCardBody } from '../../../styled/CommonStyled';
import InputWrapper from '../../../common/InputWrapper';
import PercentageInput from '../../../common/PercentageInput';
import PillSwitch from '../../../common/PillSwitch';
import { getFieldErrorStyle, renderWarningMessage } from '../../../common/FormValidation';
import type { CustomSectionProps } from '../../../../types/dynamicForm';

const SubscriptionCostsSection: React.FC<CustomSectionProps> = ({ formContext }) => {
    const { caseData, disabled, loadingStates, dispatch, handleBlur, getError } = formContext;
    const { updateCaseData } = require('../../../../store/slices/caseSetupSlice');

    const subscriptionData = (caseData as any)?.casesubscriptiondata || {};
    const salesFeePaidByInvestor = subscriptionData.salesfeepaidbyinves === true;

    const handleBooleanChange = useCallback((field: string, value: string) => {
        const boolValue = value === 'yes';
        (dispatch as Function)(updateCaseData({
            casesubscriptiondata: { ...subscriptionData, [field]: boolValue }
        }));
        handleBlur(field, boolValue).then(() => {});
    }, [subscriptionData, dispatch, handleBlur, updateCaseData]);

    const handlePercentageChange = useCallback((field: string, value: number | null) => {
        (dispatch as Function)(updateCaseData({
            casesubscriptiondata: { ...subscriptionData, [field]: value ?? undefined }
        }));
    }, [subscriptionData, dispatch, updateCaseData]);

    return (
        <Row>
            <KanbanContainer>
                <StyledCardHeader as="h6">Subscription Costs</StyledCardHeader>
                <StyledCardBody>

                    {/* Row 1 — Distribution paid by Investor */}
                    <Row>
                        <Col sm={6}>
                            <FormGroup controlId="distributionPaidByInvestor">
                                <FormLabel>Distribution paid by Investor</FormLabel>
                                <PillSwitch
                                    options={[
                                        { value: 'yes', label: 'Yes' },
                                        { value: 'no', label: 'No' }
                                    ]}
                                    value={subscriptionData.distributionpaidbyinvs === true ? 'yes' : 'no'}
                                    onChange={(value: string) => handleBooleanChange('distributionpaidbyinvs', value)}
                                    disabled={disabled}
                                    isLoading={false}
                                    name="distributionPaidByInvestor"
                                    size="sm"
                                />
                                {renderWarningMessage(getError('distributionpaidbyinvs'))}
                                <StyledFormText>Whether distribution is paid by the investor</StyledFormText>
                            </FormGroup>
                        </Col>
                    </Row>

                    {/* Row 2 — Sales Fee paid by Investor (stacked below Distribution) */}
                    <Row className="mt-3">
                        <Col sm={6}>
                            <FormGroup controlId="salesFeePaidByInvestor">
                                <FormLabel>Sales Fee paid by Investor</FormLabel>
                                <PillSwitch
                                    options={[
                                        { value: 'yes', label: 'Yes' },
                                        { value: 'no', label: 'No' }
                                    ]}
                                    value={salesFeePaidByInvestor ? 'yes' : 'no'}
                                    onChange={(value: string) => handleBooleanChange('salesfeepaidbyinves', value)}
                                    disabled={disabled}
                                    isLoading={false}
                                    name="salesFeePaidByInvestor"
                                    size="sm"
                                />
                                {renderWarningMessage(getError('salesfeepaidbyinves'))}
                                <StyledFormText>Whether sales fee is paid by the investor</StyledFormText>
                            </FormGroup>
                        </Col>
                    </Row>

                    {/* Row 3 — On Issue / On Maturity in a KanbanContainer frame, only when Sales Fee = Yes */}
                    {salesFeePaidByInvestor && (
                        <Row className="mt-3">
                            <Col sm={12}>
                                <KanbanContainer>
                                    <StyledCardHeader as="h6">Sales Fee Details</StyledCardHeader>
                                    <StyledCardBody>
                                        <Row>
                                            <Col sm={6}>
                                                <FormGroup controlId="salesNotPaidIssueDate">
                                                    <FormLabel>
                                                        On Issue date (%)
                                                    </FormLabel>
                                                    <InputWrapper
                                                        isLoading={loadingStates['salesnotpaidissuedate']}
                                                        rightIcon={renderWarningMessage(getError('salesnotpaidissuedate'))}
                                                    >
                                                        <PercentageInput
                                                            min={0}
                                                            max={100}
                                                            value={subscriptionData.salesnotpaidissuedate ?? null}
                                                            onChange={(value: number | null) => handlePercentageChange('salesnotpaidissuedate', value)}
                                                            onBlur={(value: number | null) => handleBlur('salesnotpaidissuedate', value)}
                                                            style={getFieldErrorStyle(getError('salesnotpaidissuedate'))}
                                                            disabled={disabled}
                                                        />
                                                    </InputWrapper>
                                                    <StyledFormText>Fee paid by client (0-100%)</StyledFormText>
                                                </FormGroup>
                                            </Col>
                                            <Col sm={6}>
                                                <FormGroup controlId="salesNotPaidMaturityDate">
                                                    <FormLabel>
                                                        On Maturity date (%)
                                                    </FormLabel>
                                                    <InputWrapper
                                                        isLoading={loadingStates['salesnotpaidmaturitydate']}
                                                        rightIcon={renderWarningMessage(getError('salesnotpaidmaturitydate'))}
                                                    >
                                                        <PercentageInput
                                                            min={0}
                                                            max={100}
                                                            value={subscriptionData.salesnotpaidmaturitydate ?? null}
                                                            onChange={(value: number | null) => handlePercentageChange('salesnotpaidmaturitydate', value)}
                                                            onBlur={(value: number | null) => handleBlur('salesnotpaidmaturitydate', value)}
                                                            style={getFieldErrorStyle(getError('salesnotpaidmaturitydate'))}
                                                            disabled={disabled}
                                                        />
                                                    </InputWrapper>
                                                    <StyledFormText>Fee paid by client (0-100%)</StyledFormText>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </StyledCardBody>
                                </KanbanContainer>
                            </Col>
                        </Row>
                    )}

                </StyledCardBody>
            </KanbanContainer>
        </Row>
    );
};

export default SubscriptionCostsSection;
